import React, { useState } from "react";
import Header from "../../component/Header";
import CnSidebar from "./CnSidebar";
import axios from "axios";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const STAT_OPTIONS = ["Received", "Sold", "Unpaid", "Sent", "Returned", "Repair"];

function Print() {
  const [fromOrder, setFromOrder] = useState("");
  const [toOrder, setToOrder] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);

  // --- State for Print by Status ---
  const [selectedStat, setSelectedStat] = useState("");
  const [statLoading, setStatLoading] = useState(false);
  const [statPreviewItems, setStatPreviewItems] = useState([]);

  // Parse an order number like "Mar26 001" — flexible format:
  // splits on the LAST space, everything before is the prefix, the rest is the number.
  const parseOrder = (orderNum) => {
    const trimmed = orderNum.trim();
    const lastSpace = trimmed.lastIndexOf(" ");
    if (lastSpace === -1) return null;
    const prefix = trimmed.slice(0, lastSpace);
    const numStr = trimmed.slice(lastSpace + 1);
    const num = parseInt(numStr, 10);
    if (!prefix || isNaN(num)) return null;
    return {
      prefix,                    // e.g. "Mar26"
      num,                       // e.g. 1
      padLen: numStr.length,     // e.g. 3 (preserves zero-padding)
    };
  };

  const buildOrderNumber = (prefix, num, padLen) => {
    // Rebuilds e.g. "Mar26 001"
    return `${prefix} ${String(num).padStart(padLen, "0")}`;
  };

  // Print all labels in ONE window with page breaks — avoids browser popup blocking.
  const printAllLabels = (products) => {
    const win = window.open("", "", "width=400,height=600");
    if (!win) {
      toast.error("Popup blocked! Please allow popups for this site and try again.");
      return;
    }

    const labelsHtml = products
      .map(
        (product, i) => {
          const title = [product.Brand, product.Model].filter(Boolean).join(" ");
          let specStr = "";

          if (product.frame) {
            specStr = `${product.frame}`;
          } else if (product.Ram && product.Storage) {
            specStr = `${product.Ram} / ${product.Storage}`;
          } else if (product.Ram) {
            specStr = product.Ram;
          } else if (product.Storage) {
            specStr = product.Storage;
          } else if (product.Quantity) {
            specStr = `Qty: ${product.Quantity}`;
          }

          return `
<div class="label" style="page-break-after: ${i < products.length - 1 ? "always" : "avoid"}">
  <div class="model">${title}</div>
  <div class="spec">${specStr}</div>
  <img class="barcode" src="${apiUrl}/barcodes/${product.barcode}.png"/>
  <div class="code">${product.OrderNumber || product.barcode}</div>
</div>`
        }
      )
      .join("");

    win.document.write(`
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
@page {
  size: 40mm 30mm;
  margin: 0;
}
html, body {
  width: 40mm;
  margin: 0;
  padding: 0;
  font-family: Arial, Helvetica, sans-serif;
}
.label {
  width: 40mm;
  height: 30mm;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
}
.model {
  font-size: 11px;
  font-weight: bold;
  margin-bottom: 1px;
}
.spec {
  font-size: 9px;
  margin-bottom: 2px;
}
.barcode {
  width: 34mm;
  height: 10mm;
  margin: 1px 0;
}
.code {
  font-size: 15px;
  margin-top: 1px;
  font-weight: bold;
}
<\/style>
<\/head>
<body>
${labelsHtml}
<script>
window.onload = () => {
  const imgs = Array.from(document.images);

  const triggerPrint = () => {
    setTimeout(() => {
      window.print();
    }, 300);
  };

  if (imgs.length === 0) {
    triggerPrint();
    return;
  }

  let loaded = 0;

  const onDone = () => {
    loaded++;
    if (loaded === imgs.length) {
      triggerPrint();
    }
  };

  imgs.forEach(img => {
    if (img.complete) {
      onDone();
    } else {
      img.addEventListener('load', onDone);
      img.addEventListener('error', onDone);
    }
  });
};
<\/script>
</body>
</html>
    `);
    win.document.close();
  };

  const handleBulkPrint = async () => {
    const from = parseOrder(fromOrder);
    const to = parseOrder(toOrder);

    if (!from || !to) {
      toast.error("Please enter valid order numbers (e.g. Mar26 001)");
      return;
    }

    if (from.prefix !== to.prefix) {
      toast.error(
        `Prefixes don't match: "${from.prefix}" vs "${to.prefix}". Both must share the same month/year (e.g. Apr26).`
      );
      return;
    }

    if (from.num > to.num) {
      toast.error("'From' order number must be less than or equal to 'To'");
      return;
    }

    const count = to.num - from.num + 1;
    if (count > 100) {
      toast.error("Range too large. Please print max 100 labels at a time.");
      return;
    }

    setLoading(true);
    setPreviewItems([]);

    const token = localStorage.getItem("token");
    const fetched = [];
    const failed = [];

    for (let n = from.num; n <= to.num; n++) {
      const orderNum = buildOrderNumber(from.prefix, n, from.padLen);
      try {
        const res = await axios.get(`${apiUrl}/productByOrderNumber/${encodeURIComponent(orderNum)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          fetched.push(res.data);
        }
      } catch {
        failed.push(orderNum);
      }
    }

    setLoading(false);
    setPreviewItems(fetched);

    if (failed.length > 0) {
      toast.warn(
        `${failed.length} order number(s) not found: ${failed.slice(0, 5).join(", ")}${failed.length > 5 ? "..." : ""}`
      );
    }

    if (fetched.length === 0) {
      toast.error("No products found for the given range.");
      return;
    }

    toast.success(`Printing ${fetched.length} label(s)…`);

    // All labels go into one window — no popup blocking.
    printAllLabels(fetched);
  };

  // --- Print by Status handler ---
  const handleStatPrint = async () => {
    if (!selectedStat) {
      toast.error("Please select a status first.");
      return;
    }

    setStatLoading(true);
    setStatPreviewItems([]);

    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${apiUrl}/productsByOurStat/${encodeURIComponent(selectedStat)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const items = res.data || [];
      setStatPreviewItems(items);

      if (items.length === 0) {
        toast.error(`No items found with status "${selectedStat}".`);
        setStatLoading(false);
        return;
      }

      toast.success(`Printing ${items.length} label(s) for "${selectedStat}"…`);
      printAllLabels(items);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch items by status.");
    }
    setStatLoading(false);
  };

  return (
    <>
      <Header />
      <CnSidebar />

      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 mt-10 gap-8">
        {/* ===== Existing Bulk Print Card ===== */}
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
            Bulk Label Print
          </h1>
          <p className="text-sm text-gray-500 text-center mb-6">
            Enter a range of order numbers to print all labels at once.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                From Order Number
              </label>
              <input
                type="text"
                value={fromOrder}
                onChange={(e) => setFromOrder(e.target.value)}
                placeholder="e.g. Mar26 001"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Up To Order Number
              </label>
              <input
                type="text"
                value={toOrder}
                onChange={(e) => setToOrder(e.target.value)}
                placeholder="e.g. Mar26 015"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <button
              onClick={handleBulkPrint}
              disabled={loading || !fromOrder || !toOrder}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-green-300 text-white font-semibold py-2.5 rounded-lg transition-colors duration-200"
            >
              {loading
                ? "Fetching products…"
                : `Print Labels`}
            </button>
          </div>

          {/* Preview section */}
          {previewItems.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                {previewItems.length} label(s) queued for print
              </p>
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
                {previewItems.map((item, i) => {
                  let specStr = "";
                  if (item.frame) {
                    specStr = `${item.frame}`;
                  } else if (item.Ram && item.Storage) {
                    specStr = `${item.Ram} / ${item.Storage}`;
                  } else if (item.Storage) {
                    specStr = item.Storage;
                  }

                  return (
                    <div
                      key={i}
                      className="flex flex-col px-4 py-2 text-sm text-gray-700"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {[item.Brand, item.Model].filter(Boolean).join(" ")}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {item.OrderNumber || item.barcode}
                        </span>
                      </div>
                      {specStr && (
                        <span className="text-xs text-gray-500 mt-0.5">
                          {specStr}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ===== Print by Status Card ===== */}
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
            Print by Status
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            Print all labels for items with a specific status.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Select Status
              </label>
              <select
                value={selectedStat}
                onChange={(e) => { setSelectedStat(e.target.value); setStatPreviewItems([]); }}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">-- Choose a status --</option>
                {STAT_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleStatPrint}
              disabled={statLoading || !selectedStat}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-semibold py-2.5 rounded-lg transition-colors duration-200"
            >
              {statLoading
                ? "Fetching items…"
                : `Print All "${selectedStat || '...'}" Labels`}
            </button>
          </div>

          {/* Stat preview section */}
          {statPreviewItems.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                {statPreviewItems.length} item(s) with status "{selectedStat}"
              </p>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
                {statPreviewItems.map((item, i) => {
                  let specStr = "";
                  if (item.frame) {
                    specStr = `${item.frame}`;
                  } else if (item.Ram && item.Storage) {
                    specStr = `${item.Ram} / ${item.Storage}`;
                  } else if (item.Storage) {
                    specStr = item.Storage;
                  }

                  return (
                    <div
                      key={i}
                      className="flex flex-col px-4 py-2 text-sm text-gray-700"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {[item.Brand, item.Model].filter(Boolean).join(" ")}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {item.OrderNumber || item.barcode}
                        </span>
                      </div>
                      {specStr && (
                        <span className="text-xs text-gray-500 mt-0.5">
                          {specStr}
                        </span>
                      )}
                      {item.source && (
                        <span className="text-[10px] text-gray-400 mt-0.5 italic">
                          {item.source === "otherproducts" ? "Other" : item.source === "screen" ? "Screen" : "Product"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Print;